'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'

interface CertificationsSectionProps {
  dictionary: Dictionary['home']['certifications'] // More specific type
}

export const CertificationsSection: React.FC<CertificationsSectionProps> = ({ dictionary }) => {
  return (
    <section className='py-16'>
      <div className='container mx-auto px-4'>
        <motion.div
          className='mb-12 text-center'
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className='text-sm font-medium tracking-wider text-blue-200'>{dictionary.subtitle}</span>
          <h2 className='mt-2 text-3xl font-bold tracking-tight text-slate-200 md:text-4xl'>{dictionary.title}</h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-300'>{dictionary.description}</p>
        </motion.div>

        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6'>
          <motion.div
            className='select-none flex flex-col items-center'
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <div className='flex items-center justify-center h-24 mb-4'>
              <Image
                src='/assets/images/certificates/ehedg_member_logo.png'
                alt='EHEDG'
                width={100}
                height={100}
                className='h-auto w-28 object-contain'
                draggable='false'
              />
            </div>
            <h3 className='text-lg font-bold text-slate-200 text-center'>EHEDG</h3>
            <p className='text-center text-sm text-slate-300 mt-1'>{dictionary.certificates[0].description}</p>
          </motion.div>

          <motion.div
            className='select-none flex flex-col items-center'
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <div className='flex items-center justify-center h-24 mb-4'>
              <Image
                src='/assets/images/certificates/iso-14001.png'
                alt='ISO 14001:2015'
                width={100}
                height={100}
                className='h-auto w-24 object-contain'
                draggable='false'
              />
            </div>
            <h3 className='text-lg font-bold text-slate-200 text-center'>ISO 14001:2015</h3>
            <p className='text-center text-sm text-slate-300 mt-1'>{dictionary.certificates[1].description}</p>
          </motion.div>

          <motion.div
            className='select-none flex flex-col items-center'
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <div className='flex items-center justify-center h-24 mb-4'>
              <Image
                src='/assets/images/certificates/iso-45001.png'
                alt='ISO 45001:2018'
                width={100}
                height={100}
                className='h-auto w-28 object-contain'
                draggable='false'
              />
            </div>
            <h3 className='text-lg font-bold text-slate-200 text-center'>ISO 45001:2018</h3>
            <p className='text-center text-sm text-slate-300 mt-1'>{dictionary.certificates[2].description}</p>
          </motion.div>

          <motion.div
            className='select-none flex flex-col items-center'
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <div className='flex items-center justify-center h-24 mb-4'>
              <Image
                src='/assets/images/certificates/iso-9001.png'
                alt='ISO 9001:2015'
                width={100}
                height={100}
                className='h-auto w-24 object-contain'
                draggable='false'
              />
            </div>
            <h3 className='text-lg font-bold text-slate-200 text-center'>ISO 9001:2015</h3>
            <p className='text-center text-sm text-slate-300 mt-1'>{dictionary.certificates[3].description}</p>
          </motion.div>

          <motion.div
            className='select-none flex flex-col items-center'
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <div className='flex items-center justify-center h-24 mb-4'>
              <Image
                src='/assets/images/certificates/iso-10002.png'
                alt='ISO 10002:2018'
                width={100}
                height={100}
                className='h-auto w-28 object-contain'
                draggable='false'
              />
            </div>
            <h3 className='text-lg font-bold text-slate-200 text-center'>ISO 10002:2018</h3>
            <p className='text-center text-sm text-slate-300 mt-1'>{dictionary.certificates[4].description}</p>
          </motion.div>

          <motion.div
            className='select-none flex flex-col items-center'
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className='flex items-center justify-center h-24 mb-4'>
              <Image
                src='/assets/images/certificates/gcr_cert.png'
                alt='GCR Certificate'
                width={100}
                height={100}
                className='h-auto w-32 object-contain'
                draggable='false'
              />
            </div>
            <h3 className='text-lg font-bold text-slate-200 text-center'>GCR</h3>
            <p className='text-center text-sm text-slate-300 mt-1'>{dictionary.certificates[5].description}</p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
